export interface XYPosition {
  x: number;
  y: number;
}

export interface BoundingBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

export class CoordinateUtils {
  static calculateBounds(
    nodes: Array<{ x: number; y: number; width?: number; height?: number }>,
    padding: number = 0
  ): BoundingBox {
    if (nodes.length === 0) {
      return { x: 0, y: 0, width: 0, height: 0 };
    }

    let minX = Infinity, minY = Infinity;
    let maxX = -Infinity, maxY = -Infinity;

    nodes.forEach(node => {
      const w = node.width ?? 280;
      const h = node.height ?? 200;
      minX = Math.min(minX, node.x);
      minY = Math.min(minY, node.y);
      maxX = Math.max(maxX, node.x + w);
      maxY = Math.max(maxY, node.y + h);
    });

    return {
      x: minX - padding,
      y: minY - padding,
      width: maxX - minX + padding * 2,
      height: maxY - minY + padding * 2
    };
  }

  static isPointInBounds(
    point: XYPosition,
    bounds: BoundingBox,
    buffer: number = 0
  ): boolean {
    return (
      point.x >= bounds.x - buffer &&
      point.x <= bounds.x + bounds.width + buffer &&
      point.y >= bounds.y - buffer &&
      point.y <= bounds.y + bounds.height + buffer
    );
  }

  static isRectIntersect(
    rect1: BoundingBox,
    rect2: BoundingBox
  ): boolean {
    return (
      rect1.x < rect2.x + rect2.width &&
      rect1.x + rect1.width > rect2.x &&
      rect1.y < rect2.y + rect2.height &&
      rect1.y + rect1.height > rect2.y
    );
  }

  static isRectContains(
    container: BoundingBox,
    inner: BoundingBox,
    buffer: number = 0
  ): boolean {
    return (
      inner.x >= container.x - buffer &&
      inner.x + inner.width <= container.x + container.width + buffer &&
      inner.y >= container.y - buffer &&
      inner.y + inner.height <= container.y + container.height + buffer
    );
  }

  static getNodeCenter(
    node: { x: number; y: number; width?: number; height?: number }
  ): XYPosition {
    return {
      x: node.x + (node.width ?? 280) / 2,
      y: node.y + (node.height ?? 200) / 2
    };
  }

  static getNodeBounds(
    node: { x: number; y: number; width?: number; height?: number }
  ): BoundingBox {
    return {
      x: node.x,
      y: node.y,
      width: node.width ?? 280,
      height: node.height ?? 200
    };
  }

  static screenToCanvas(
    screenPos: XYPosition,
    viewport: { x: number; y: number; zoom: number }
  ): XYPosition {
    return {
      x: (screenPos.x - viewport.x) / viewport.zoom,
      y: (screenPos.y - viewport.y) / viewport.zoom
    };
  }

  static canvasToScreen(
    canvasPos: XYPosition,
    viewport: { x: number; y: number; zoom: number }
  ): XYPosition {
    return {
      x: canvasPos.x * viewport.zoom + viewport.x,
      y: canvasPos.y * viewport.zoom + viewport.y
    };
  }

  static calculateResize(
    bounds: BoundingBox,
    corner: string,
    delta: XYPosition,
    minSize: { width: number; height: number } = { width: 200, height: 150 },
    keepRatio: boolean = false
  ): BoundingBox {
    let { x, y, width, height } = bounds;

    if (keepRatio) {
      const maxDelta = Math.max(Math.abs(delta.x), Math.abs(delta.y));
      const signX = Math.sign(delta.x);
      const signY = Math.sign(delta.y);
      delta = { x: maxDelta * signX, y: maxDelta * signY };
    }

    switch (corner) {
      case 'nw':
        x += delta.x;
        y += delta.y;
        width -= delta.x;
        height -= delta.y;
        break;
      case 'n':
        y += delta.y;
        height -= delta.y;
        break;
      case 'ne':
        width += delta.x;
        y += delta.y;
        height -= delta.y;
        break;
      case 'w':
        x += delta.x;
        width -= delta.x;
        break;
      case 'e':
        width += delta.x;
        break;
      case 'sw':
        x += delta.x;
        width -= delta.x;
        height += delta.y;
        break;
      case 's':
        height += delta.y;
        break;
      case 'se':
        width += delta.x;
        height += delta.y;
        break;
    }

    if (width < minSize.width) {
      if (corner.includes('w') || corner === 'nw' || corner === 'sw' || corner === 'w') {
        x -= minSize.width - width;
      }
      width = minSize.width;
    }
    if (height < minSize.height) {
      if (corner.includes('n') || corner === 'nw' || corner === 'ne' || corner === 'n') {
        y -= minSize.height - height;
      }
      height = minSize.height;
    }

    return { x, y, width, height };
  }

  static absoluteToRelative(
    absolutePos: XYPosition,
    parentPos: XYPosition
  ): XYPosition {
    return {
      x: absolutePos.x - parentPos.x,
      y: absolutePos.y - parentPos.y
    };
  }

  static relativeToAbsolute(
    relativePos: XYPosition,
    parentPos: XYPosition
  ): XYPosition {
    return {
      x: relativePos.x + parentPos.x,
      y: relativePos.y + parentPos.y
    };
  }
}
